import { isTheme, normalizeLanguage, type AppLanguage, type AppTheme } from './catalog';

export interface PreferencesState { language: AppLanguage; theme: AppTheme }
export interface PreferenceTransport {
  platformApi: string;
  getAccessToken: () => string | null;
  fetchImpl?: typeof fetch;
}
const KEY = 'architech.preferences.v1';
const COOKIE = 'arch_prefs';
/** Focus, tab switches and the timer re-read the cookie (free); the account copy at most this often. */
const SERVER_READ_INTERVAL_MS = 5 * 60_000;
const listeners = new Set<() => void>();
let transport: PreferenceTransport | null = null;
let owner: string | null = null;
let pending: Partial<PreferencesState> = {};
let revision = 0;
let inflight: Promise<void> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let lastServerRead = 0;

function stored(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function parse(raw: string | null): Partial<PreferencesState> {
  try {
    const value = JSON.parse(raw ?? 'null');
    return value && typeof value === 'object' ? {
      ...(isTheme(value.theme) ? { theme: value.theme } : {}),
      ...(normalizeLanguage(value.language) ? { language: normalizeLanguage(value.language)! } : {}),
    } : {};
  } catch { return {}; }
}
/** When the stored value was chosen (ms); 0 for values written before stamps existed. */
function stampOf(raw: string | null): number {
  try { const at = JSON.parse(raw ?? 'null')?.at; return typeof at === 'number' && Number.isFinite(at) ? at : 0; } catch { return 0; }
}
/**
 * The newest `arch_prefs` cookie. A browser can hold two: the shared `.biznesi.al` one and an old
 * host-only one written before the domain move. Taking the first one listed re-applied the stale
 * theme on every focus/tick and the account read switched it back: the screen flashed.
 */
function readCookie(): { value: Partial<PreferencesState>; at: number; conflicting: boolean } {
  try {
    const entries = document.cookie.split('; ').filter(item => item.startsWith(`${COOKIE}=`))
      .map(item => decodeURIComponent(item.slice(COOKIE.length + 1)))
      .map(raw => ({ value: parse(raw), at: stampOf(raw) }));
    if (!entries.length) return { value: {}, at: 0, conflicting: false };
    const newest = entries.reduce((best, entry) => entry.at > best.at ? entry : best);
    const conflicting = entries.some(entry => JSON.stringify(entry.value) !== JSON.stringify(newest.value));
    return { ...newest, conflicting };
  } catch { return { value: {}, at: 0, conflicting: false }; }
}
function initial(): { state: PreferencesState; at: number } {
  const localRaw = stored(KEY);
  const local = parse(localRaw);
  const found = readCookie();
  // Unstamped cookies that disagree cannot be ordered: this origin's own copy decides.
  const cookie = found.conflicting && found.at === 0 ? {} : found.value;
  const legacyTheme = stored('app_theme_v2') ?? stored('cosmetics_theme_v1') ?? stored('mainLandingTheme') ?? stored('publicMenuTheme');
  return {
    state: {
      theme: cookie.theme ?? local.theme ?? (isTheme(legacyTheme) ? legacyTheme : 'light'),
      language: cookie.language ?? local.language ?? normalizeLanguage(stored('i18nextLng') ?? stored('cosmetics_language_v1')) ?? 'al',
    },
    at: Math.max(found.at, stampOf(localRaw)),
  };
}
const start = initial();
let state = start.state;
let stamp = start.at;

function persist() {
  const value = JSON.stringify({ ...state, at: stamp });
  try {
    localStorage.setItem(KEY, value);
    // Compatibility for i18next detectors and legacy code while SSO migrates.
    localStorage.setItem('i18nextLng', state.language);
    localStorage.setItem('app_theme_v2', state.theme);
  } catch { /* Preferences remain available in memory. */ }
  const host = location.hostname;
  const shared = host === 'biznesi.al' || host.endsWith('.biznesi.al');
  try {
    // Remove the host-only copy so only the shared cookie is left to read.
    if (shared) document.cookie = `${COOKIE}=; Path=/; Max-Age=0`;
    document.cookie = `${COOKIE}=${encodeURIComponent(value)}; Path=/; Max-Age=31536000; SameSite=Lax${shared ? '; Domain=.biznesi.al' : ''}${location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch { /* Cookie storage may be blocked. */ }
}
function apply(next: Partial<PreferencesState>, at = Date.now()) {
  if ((!next.theme || next.theme === state.theme) && (!next.language || next.language === state.language)) return;
  state = { ...state, ...next };
  stamp = at;
  persist();
  listeners.forEach(listener => listener());
}
function identity(): { userId: string; token: string } | null {
  const token = transport?.getAccessToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    // Preference sync is optional. Do not send an expired cached access token;
    // the host session refresh will call refreshPreferences with a fresh token.
    if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) return null;
    return typeof payload.sub === 'string' ? { userId: payload.sub, token } : null;
  } catch { return null; }
}

async function sync() {
  const active = identity();
  if (!active || !transport) { owner = null; pending = {}; return; }
  if (owner !== active.userId) { owner = active.userId; pending = {}; revision++; }
  const config = transport;
  const sentRevision = revision;
  const patch = { ...pending };
  const writing = Object.keys(patch).length > 0;
  lastServerRead = Date.now();
  try {
    const response = await (config.fetchImpl ?? fetch)(`${config.platformApi.replace(/\/+$/, '')}/me/ui-preferences`, {
      method: writing ? 'PATCH' : 'GET',
      credentials: 'omit',
      headers: { Authorization: `Bearer ${active.token}`, ...(writing ? { 'Content-Type': 'application/json' } : {}) },
      ...(writing ? { body: JSON.stringify(patch) } : {}),
    });
    if (!response.ok) return; // Keep local choices; retry on focus/tick, including rollout 503.
    const data = parse(JSON.stringify(await response.json()));
    if (identity()?.userId !== active.userId || transport !== config) return;
    if (revision === sentRevision) { pending = {}; apply(data); }
    else apply({ ...data, ...pending }); // An earlier response cannot overwrite a newer choice.
  } catch { /* Offline: keep local choices and the pending patch for a later retry. */ }
}
export function refreshPreferences(): Promise<void> {
  if (inflight) return inflight;
  inflight = sync().finally(() => { inflight = null; });
  return inflight;
}
export function configurePreferences(config: PreferenceTransport) {
  transport = config;
  owner = null;
  pending = {};
  void refreshPreferences();
}
function watch() {
  // Another app on .biznesi.al changed it more recently than this page did.
  const cookie = readCookie();
  if (cookie.at > stamp) apply(cookie.value, cookie.at);
  // Another device: the account copy, read at most every few minutes (unsent changes retry sooner).
  const due = Object.keys(pending).length > 0 || Date.now() - lastServerRead >= SERVER_READ_INTERVAL_MS;
  if (document.visibilityState !== 'hidden' && due) void refreshPreferences();
}
export const preferences = {
  getSnapshot: () => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    if (listeners.size === 1) {
      window.addEventListener('focus', watch);
      window.addEventListener('storage', watch);
      document.addEventListener('visibilitychange', watch);
      timer = setInterval(watch, 30_000);
      watch();
      persist(); // Publish migrated legacy choices even before the user changes a control.
    }
    return () => {
      listeners.delete(listener);
      if (!listeners.size) {
        window.removeEventListener('focus', watch);
        window.removeEventListener('storage', watch);
        document.removeEventListener('visibilitychange', watch);
        if (timer) clearInterval(timer);
        timer = null;
      }
    };
  },
  setTheme(theme: AppTheme) { if (isTheme(theme)) update({ theme }); },
  setLanguage(language: string) { const value = normalizeLanguage(language); if (value) update({ language: value }); },
};
function update(patch: Partial<PreferencesState>) {
  if ((!patch.theme || patch.theme === state.theme) && (!patch.language || patch.language === state.language)) return;
  apply(patch);
  const active = identity();
  if (active) {
    if (owner !== active.userId) { owner = active.userId; pending = {}; }
    pending = { ...pending, ...patch };
    revision++;
    void refreshPreferences().then(() => {
      if (Object.keys(pending).length) void refreshPreferences();
    });
  }
}
